/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AccessOverridesStorageObject} from '../../models/AccessOverrides';
import type {CoreContext} from '../../models/Context';
import {
  FeatureFlag,
  FeatureFlagPayload,
  FeatureFlagUser,
  LaunchDarklyFeatureFlagsResponse,
  ProjectFeatureFlags
} from '@exc/graphql/src/models/launchDarkly';
import type {FeatureFlagConfig} from '@adobe/exc-app/featureflags';
import type {
  FeatureFlagOptions,
  FlattenedFeatureFlag,
  OverridesStorageContext
} from '../../models/LaunchDarkly';
import type {FeatureFlagsById, GetFlagsContext, GraphQLPayload} from '../../models/FeatureFlags';
import FeatureFlagService from './FeatureFlagService';
import {FORCIBLE_PROJECTS, ORG_SANDBOX_PROJECTS, VALID_LAUNCH_DARKLY_PROJECTS} from '../../utils/launchDarkly';
import {generateUserHash} from '../../utils';
import {getMultiProjectFeatureFlags} from '@exc/graphql/src/queries/launchDarkly';
import {mergeFeatureFlagsForShell} from '../../modules/DebugModules/AccessOverrides/common';
import {PROVIDERS} from '@adobe/exc-app/featureflags';
import {storage} from '@exc/storage';

class LaunchDarklyService extends FeatureFlagService {
  constructor() {
    super(PROVIDERS.LAUNCH_DARKLY, VALID_LAUNCH_DARKLY_PROJECTS);
  }

  /**
   * Cleans up the array of projects' features flags.
   * @param {ProjectFeatureFlags[]} data - Array of feature flag objects
   * @returns {FeatureFlagsById} - A flattened object of projects with feature flag properties
   */
  flattenFeatureFlags(data: ProjectFeatureFlags[]): FeatureFlagsById {
    const flattenedFlags: FeatureFlagsById = {};
    // Go through each project's array of feature flags and set as objects
    data.forEach(({projectId, featureFlags}: ProjectFeatureFlags) => {
      flattenedFlags[projectId] = {};
      featureFlags.forEach(({name, value}: FeatureFlag) => {
        flattenedFlags[projectId][name] = value;
      });
    });
    // Returned a flattened object
    return flattenedFlags;
  }

  /**
   * Merges feature flag response data with overrides for development and testing
   * @param {ProjectFeatureFlags[]} projectFeatureFlags - Response of feature flags by project id
   * @param {string} hashedAuthId - User hash string used in storage of override values
   * @returns {FeatureFlagsById} - A flattened object of projects with feature flag properties
   */
  async overrideFeatureFlags(
    projectFeatureFlags: FeatureFlagsById,
    hashedAuthId: string
  ): Promise<FeatureFlagsById> {
    const storedData = await storage.local.get('accessOverrides') as AccessOverridesStorageObject;
    const featureFlags = storedData?.[hashedAuthId]?.['featureFlags'] || {};
    const mergedFlags = {...projectFeatureFlags};
    for (const [key, value] of Object.entries(featureFlags)) {
      mergedFlags[key] = mergeFeatureFlagsForShell(value, projectFeatureFlags[key]) as FlattenedFeatureFlag;
    }
    return mergedFlags;
  }

  /**
   * Cleans up and sorts response for multiple flag requests
   * @param {Array} projectIds - Array of LaunchDarkly project IDs
   * @param {Object} projectFeatureFlags - Response of feature flags by project ID (possibly from cache)
   * @param {OverridesStorageContext} context - Core context values needed to deliver the data
   * @returns {FeatureFlagsById} - An object of project ids with flattened feature flags
   */
  public async formatResponse(
    projectIds: string[],
    context: OverridesStorageContext,
    projectFeatureFlags: FeatureFlagsById = {}
  ): Promise<FeatureFlagsById> {
    // Support internal feature flag overrides for runtime API
    const {accessOverridesEnabled, hashedAuthId} = context;
    // Limited to internal users and overrides set to on/enabled
    if (accessOverridesEnabled) {
      projectFeatureFlags = await this.overrideFeatureFlags(projectFeatureFlags, hashedAuthId);
    }
    // Because the response could include ALL the projects
    // We filter the list by just the requested projects first
    return Object.keys(projectFeatureFlags)
      .sort((a, b) => a.localeCompare(b))
      .reduce((result: FeatureFlagsById, key) => projectIds.includes(key) ? {...result, [key]: projectFeatureFlags[key]} : result, {});
  }

  /**
   * Cleans up the response, caches and returns it.
   * @param {string} key - unique key for type of feature flag query
   * @param {GraphQlResponse} graphQLResponse - Multiple project response from GraphQl
   * @param {string} projectId - Requests for one project have an id, defaults to ''
   * @returns {FeatureFlagsById} - An object of project ids with flattened feature flags
   */
  public processSuccessResponse(
    key: string,
    graphQLResponse: LaunchDarklyFeatureFlagsResponse,
    projectIds: string[]
  ): FeatureFlagsById {
    // Shape the response data
    const {multiProjectFeatureFlags: {projects}} = graphQLResponse;
    const flattenedFlags: FeatureFlagsById = this.flattenFeatureFlags(projects);
    this.updateCache(key, flattenedFlags, projectIds);
    // Return the flags sorted by project so we're handling similar object shapes
    return flattenedFlags;
  }

  /**
   * Checks context data to see if the required data is missing.
   * @param {Array} projectIds - LaunchDarkly project ids strings
   * @param {CoreContext} context - Core Context
   * @returns {boolean} False if context has all data needed, true otherwise.
   */
  public isContextMissing(projectIds: string[], context: CoreContext): boolean {
    const missingContext = this.isCoreContextMissing(context);
    if (missingContext) {
      return missingContext;
    }
    const {sandbox} = context;
    // Some LaunchDarkly requests require a sandbox name string
    const sandboxRequired = projectIds.some(project => ORG_SANDBOX_PROJECTS.includes(project));
    return sandboxRequired && !sandbox;
  }

  /**
   * Formats the options payload for the GraphQL request.
   * @param {Array} projectIds - LaunchDarkly project ids strings
   * @param {GetFlagsContext} context - Core Context
   * @returns {LaunchDarklyInput} - An object of project ids with context data
   * @public
   */
  public getProviderPayload(
    projectIds: string[],
    options: FeatureFlagUser<FeatureFlagPayload>
  ): GraphQLPayload {
    return {projectIds, userData: options};
  }

  /**
   * A wrapping function to support the bulk API with a list of project ids
   * as well as clean up and sort the returned object
   * @param {Array} projectIds - Array of strings of project IDs or ['*'] for all
   * @param {GetFlagsContext} context - Current context object that includes options we need
   * @returns {FeatureFlagsById} - An object of project ids with flattened feature flags
   */
  async getFeatureFlags(
    projectIds: string[],
    context: GetFlagsContext,
    config?: FeatureFlagConfig
  ): Promise<FeatureFlagsById | undefined> {
    const {
      accessOverridesEnabled = false,
      hashedAuthId,
      imsOrg: imsOrgId,
      internal,
      locale,
      imsInfo,
      sandbox
    } = context;
    // Validate the project ids and if some are invalid
    // the base class throws an error just like before
    projectIds = this.validateProjectIds(projectIds) as string[];
    // If there are no project ids, return undefined
    if (projectIds.length === 0) {
      return undefined;
    }
    // Construct the graphql query options
    const options: FeatureFlagOptions = {
      imsOrgId,
      internal,
      locale,
      sandboxName: sandbox?.name,
      sandboxRegion: sandbox?.region
    };
    // Add the email to the options if it set.
    imsInfo?.imsProfile?.email && (options.userHash = generateUserHash(imsInfo.imsProfile.email));
    // Some projects require the org AND sandbox in the request key (i.e. CJM)
    const key = sandbox ? `${options.imsOrgId}/${options.sandboxName}/${options.sandboxRegion}` : imsOrgId;
    const userData: FeatureFlagUser<FeatureFlagPayload> = {
      custom: options
    };
    // Get the feature flags
    return this.getFlagData(
      getMultiProjectFeatureFlags as (payload: GraphQLPayload) => Promise<LaunchDarklyFeatureFlagsResponse>,
      projectIds,
      userData,
      key,
      FORCIBLE_PROJECTS,
      config
    ).then(flagData => this.formatResponse(
      projectIds,
      {accessOverridesEnabled, hashedAuthId},
      flagData
    ));
  }
}

export default new LaunchDarklyService();
